import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { OpenClose, PopupBaseComponent } from '@app/shared/popup';
import { UserService } from '@app/user';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { LeaderboardTrialInvitesPopupService } from './service/leaderboard-trial-invites-popup.service';

@Component({
    selector: 'app-leaderboard-trial-invites-popup',
    templateUrl: './leaderboard-trial-invites-popup.component.html',
    styleUrls: ['./leaderboard-trial-invites-popup.component.scss'],
    animations: [OpenClose],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LeaderboardTrialInvitesPopupService],
    standalone: false,
})
export class LeaderboardTrialInvitesPopupComponent extends PopupBaseComponent implements OnInit, OnDestroy {
    private readonly userService = inject(UserService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly leaderboardTrialInvitesPopupService = inject(LeaderboardTrialInvitesPopupService);

    fullName = new UntypedFormControl('', [Validators.required, Validators.pattern(/^(?:[\s]{0,61})([\S]{1,61}\s{1,61}[\S]{1,61}){1,}(?:[\S\s]{0,61})$/)]);
    email = new UntypedFormControl('', [
        Validators.required,
        Validators.pattern(
            /^(?:[\s]{0,61})[a-zA-Z0-9.!#$%&’'*+\\/=?^_`‘{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)(?:[\s]{0,61})+$/,
        ),
    ]);

    hoverIndex: number | null = null;

    private subscriptions = new Subscription();

    ngOnInit(): void {
        this.subscriptions.add(
            this.invitesData$.subscribe((data) => {
                if (data.closePopup) {
                    this.closePopup();
                }
                this.changeDetectorRef.detectChanges();
            }),
        );
        this.subscriptions.add(this.invitesTrial$.subscribe(() => this.changeDetectorRef.detectChanges()));
        this.leaderboardTrialInvitesPopupService.init();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.leaderboardTrialInvitesPopupService.destroy();
    }

    sendInvites() {
        if (!this.canSendInvites) {
            return;
        }
        this.leaderboardTrialInvitesPopupService.sendInvites();
    }

    get invitesData$() {
        return this.leaderboardTrialInvitesPopupService.invitesData$;
    }

    get addedUsers$() {
        return this.invitesData$.pipe(map((data) => data.users));
    }

    get loaded$() {
        return this.invitesData$.pipe(map((data) => data.loaded));
    }

    get loadedTrial$() {
        return this.invitesTrial$.pipe(map((data) => data.loaded));
    }

    get loading$() {
        return this.invitesData$.pipe(map((data) => data.loading));
    }

    get invitesTrial$() {
        return this.leaderboardTrialInvitesPopupService.invitesTrial$;
    }

    get invitesTrial() {
        return this.leaderboardTrialInvitesPopupService.invitesTrial;
    }

    get maxInvites$() {
        return this.invitesTrial$.pipe(map((data) => data.max));
    }

    get invitesData() {
        return this.leaderboardTrialInvitesPopupService.invitesData;
    }

    get companyName() {
        return (<any>this.userService.user).company.name;
    }

    get showAddAnotherBtn() {
        return this.fullNameHasValue || this.emailHasValue || this.hasAddedUsers;
    }

    get hideAddAnotherBtn() {
        return this.hasAddedUsers && !this.fullNameHasValue && !this.emailHasValue;
    }

    get messageControl() {
        return this.leaderboardTrialInvitesPopupService.messageControl;
    }

    get fullNameHasValue() {
        return this.fullName.value.length > 0;
    }

    get emailHasValue() {
        return this.email.value.length > 0;
    }

    get nameAndEmailValid() {
        return this.fullName.valid && this.email.valid && !this.companyHasEmail;
    }

    get hasAddedUsers() {
        return this.invitesData.users.length > 0;
    }

    get canSendInvites() {
        return this.invitesTrial.sent < this.invitesData.users.length && this.leaderboardTrialInvitesPopupService.usersToSentCount > 0;
    }

    get companyHasEmail() {
        return this.invitesData.users.find((user: any) => user.email === this.email.value) || (<any>this.userService.user).email === this.email.value;
    }

    closePopup() {
        this.close('close');
    }

    addAnother() {
        this.email.markAsTouched();
        this.fullName.markAsTouched();
        if (!this.nameAndEmailValid) {
            return;
        }
        const fullName = this.fullName.value.replace(/  +/g, ' ').trim();
        const email = this.email.value.replace(/  +/g, ' ').trim();
        const user = {
            name: fullName,
            email: email,
        };
        this.leaderboardTrialInvitesPopupService.addUser(user);
        this.clearFullNameAndEmail();
    }

    removeUser(index: number) {
        this.leaderboardTrialInvitesPopupService.removeUser(index);
    }

    clearFullNameAndEmail() {
        this.email.setValue('');
        this.fullName.setValue('');
        this.email.markAsUntouched();
        this.fullName.markAsUntouched();
        this.changeDetectorRef.detectChanges();
    }

    onMouseEnter(index: number) {
        this.hoverIndex = index;
    }

    onMouseLeave() {
        this.hoverIndex = null;
    }
}
